import React, {ChangeEvent, useCallback, useState} from 'react';
import {BaseInputProps} from '../..';

import {Tooltip} from '@mantine/core';
import {ErrorOption, useController, useFormContext} from 'react-hook-form';
import {RiInformationFill} from 'react-icons/ri';
import {NestedKeys} from 'shared/types/key-manipulation';
import Mask, {MaskProps} from 'shared/util/mask';
import {Container, ContainerLabel, Label, Input as StyledInput} from './styles';
import ErrorWrapper from '../error-wrapper';

export type InputProps<T = any> = BaseInputProps<T> & {
  name: NestedKeys<T>;
  gridColumn?: number;
  secureText?: boolean;
  mask?: MaskProps;
};

const Input: React.FC<InputProps> = ({
  name,
  label,
  gridColumn,
  border,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  hidden,
  secureText,
  information,
  mask,
  required,
  ...props
}) => {
  const {control, setValue, clearErrors, formState} = useFormContext();
  const {field} = useController({control, name: name!});
  const [focused, setFocused] = useState(false);

  const error: ErrorOption | undefined = formState.errors
    ? formState.errors[name as string]
    : undefined;

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    clearErrors(name);
    let value = event.target.value;

    if (mask) {
      value = Mask.unMask(value);

      if (name == 'phone') {
        value = value.slice(0, 11);
      }
    }

    setValue(name, value);
  };

  const getValue = useCallback(
    (value: string) => {
      if (!value) return '';
      if (!mask) return value;

      return Mask.format(mask, value);
    },
    [mask],
  );

  return (
    <ErrorWrapper {...{error: error?.message, gridColumn}}>
      <Container {...{border, focused}}>
        <ContainerLabel>
          {label && (
            <Label {...{error: !!error}}>
              {label}
              {required && ' *'}
            </Label>
          )}
          {information && (
            <Tooltip
              label={information}
              multiline
              withArrow
              arrowSize={10}
              openDelay={500}
              w={200}
              position="top">
              <div>
                <RiInformationFill size={20} />
              </div>
            </Tooltip>
          )}
        </ContainerLabel>
        <StyledInput
          {...{
            ...props,
            ...field,
            value: getValue(field.value),
            onFocus: () => setFocused(true),
            onBlur: () => setFocused(false),
            type: secureText ? 'password' : 'text',
            onChange,
            error: !!error,
            autoComplete: 'nope',
          }}
        />
      </Container>
    </ErrorWrapper>
  );
};

export default Input;
